// v16.17 Phase C.40 (Feb 2026) — Publisher Battery Monitor hook.
//
// Mount ở root (App.js FE). Chạy trên MỌI trang của app (Android WebView
// coi web SPA là toàn bộ app). Chỉ actively push heartbeat nếu:
//   • User authenticated.
//   • roles bao gồm "publisher" (không kén publisher-thuần vì mọi user có
//     role publisher đều có thể ở role phát).
//
// Nguồn dữ liệu pin:
//   • navigator.getBattery() (Web Battery API) — hoạt động ok Android
//     WebView Chrome 4+. Không có ở iOS Safari — sẽ silent no-op.
//   • Từ postMessage bridge Native `BATTERY_UPDATE` (từ App.js RN — Phase
//     C.40 tầng 2). Native fire khi expo-battery levelChange event.
//
// Chính sách push:
//   • Push MỖI 60 GIÂY trong khi có battery data (heartbeat để BE biết
//     device còn sống).
//   • Push NGAY khi level giảm ≥ 5% hoặc charging state đổi (event-driven).
//   • Không push khi tab hidden > 5 phút (tiết kiệm khi user rời máy).
import { useEffect, useRef } from "react";
import { api } from "../lib/api";

const HEARTBEAT_MS = 60000;
const SIGNIFICANT_DROP = 5;

function readArmedConfigId() {
  try {
    const raw = window.localStorage.getItem("pl.pub.auto.armed");
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.config_id || null;
  } catch {
    return null;
  }
}

function readActiveSessionId() {
  try {
    const raw = window.localStorage.getItem("pl.pub.native.lastActiveSession");
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.session_id || null;
  } catch {
    return null;
  }
}

export default function usePublisherBatteryMonitor(user) {
  const lastPushRef = useRef({ level: null, isCharging: null, at: 0, source: "web" });
  const timerRef = useRef(null);
  const nativeBatteryRef = useRef(null); // từ postMessage native

  const hasPublisherRole = !!user && Array.isArray(user.roles) && user.roles.includes("publisher");

  useEffect(() => {
    if (!hasPublisherRole) return undefined;

    let batteryObj = null;
    let cancelled = false;

    const pushHeartbeat = async (level, isCharging, source) => {
      if (cancelled) return;
      // Debounce: skip nếu vừa push dưới 5s trước với cùng level.
      const now = Date.now();
      const last = lastPushRef.current;
      if (last.at && now - last.at < 5000 && last.level === level && last.isCharging === isCharging) {
        return;
      }
      lastPushRef.current = { level, isCharging, at: now, source };
      try {
        // v17.19 — Traffic control: tắt giám sát pin.
        const { getTrafficFlag } = require("../lib/trafficFlags");
        if (!getTrafficFlag("publisher_battery")) return;
      } catch { /* noop */ }
      try {
        await api.post("/publisher/battery/heartbeat", {
          level,
          is_charging: isCharging,
          source,
          config_id: readArmedConfigId(),
          session_id: readActiveSessionId(),
          device_name: user?.username || user?.full_name || null,
        });
      } catch { /* silent — BE lỗi không nên phá UX */ }
    };

    const pickCurrentReading = () => {
      // Ưu tiên native nếu có (source-of-truth cấp OS), fallback web Battery API.
      const nb = nativeBatteryRef.current;
      if (nb && typeof nb.level === "number") {
        return { level: nb.level, isCharging: !!nb.isCharging, source: "native" };
      }
      if (batteryObj && typeof batteryObj.level === "number") {
        return {
          level: Math.round(batteryObj.level * 100),
          isCharging: !!batteryObj.charging,
          source: "web",
        };
      }
      return null;
    };

    const tick = () => {
      const r = pickCurrentReading();
      if (r) pushHeartbeat(r.level, r.isCharging, r.source);
    };

    const onBatteryChange = () => {
      const r = pickCurrentReading();
      if (!r) return;
      const last = lastPushRef.current;
      const dropped = last.level != null && r.level <= last.level - SIGNIFICANT_DROP;
      const chargeChanged = last.isCharging !== null && last.isCharging !== r.isCharging;
      if (dropped || chargeChanged || last.level === null) {
        pushHeartbeat(r.level, r.isCharging, r.source);
      }
    };

    // Native RN postMessage bridge (Phase C.40 tầng 2).
    const onNativeMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || data.type !== "BATTERY_UPDATE") return;
        nativeBatteryRef.current = { level: data.level, isCharging: !!data.isCharging };
        onBatteryChange();
      } catch { /* noop */ }
    };
    window.addEventListener("message", onNativeMessage);
    document.addEventListener("message", onNativeMessage);

    // Kick off Web Battery API listeners.
    if (typeof navigator !== "undefined" && typeof navigator.getBattery === "function") {
      navigator.getBattery().then((b) => {
        if (cancelled) return;
        batteryObj = b;
        b.addEventListener("levelchange", onBatteryChange);
        b.addEventListener("chargingchange", onBatteryChange);
        // First push ngay khi có data.
        onBatteryChange();
      }).catch(() => { /* iOS / unsupported */ });
    }

    // Periodic heartbeat.
    timerRef.current = setInterval(tick, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("message", onNativeMessage);
      document.removeEventListener("message", onNativeMessage);
      if (batteryObj) {
        try {
          batteryObj.removeEventListener("levelchange", onBatteryChange);
          batteryObj.removeEventListener("chargingchange", onBatteryChange);
        } catch { /* noop */ }
      }
    };
  }, [hasPublisherRole, user]);
}
